import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge
} from '@mui/material';
import { styled } from '@mui/system';
import {
  Person,
  Phone,
  Email,
  CalendarToday,
  Home,
  Description,
  AttachFile,
  CheckCircle,
  Pending,
  Cancel,
  Map,
  ArrowBack,
  Download
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetScheduledVisitByIdQuery } from './api/scheduledVisitApi';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
//   boxShadow: theme.shadows[2],
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
}));

const StatusBadge = styled(Chip)(({ theme, status }: { theme?: any; status: string }) => ({
  fontWeight: 'bold',
  ...(status === 'confirmed' && {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  }),
  ...(status === 'pending' && {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  }),
  ...(status === 'cancelled' && {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  }),
}));

const ScheduledVisitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, error, isLoading } = useGetScheduledVisitByIdQuery(id!);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">Error loading visit details</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box mt={4}>
        <Alert severity="warning">Visit not found</Alert>
      </Box>
    );
  }

  const visit = data.data;
  const scheduledDate = new Date(visit.scheduled_at);
  const formattedDate = scheduledDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const getStatusIcon = () => {
    switch (visit.status) {
      case 'confirmed':
        return <CheckCircle color="success" />;
      case 'pending':
        return <Pending color="warning" />;
      case 'cancelled':
        return <Cancel color="error" />;
      default:
        return <Pending color="warning" />;
    }
  };

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back to Visits
      </Button>

      {/* Visit Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            <Person sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {visit.visitor_first_name} {visit.visitor_last_name}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Visit Scheduled for {formattedDate}
            </Typography>
          </Box>
        </Box>
        <StatusBadge
          status={visit.status}
          icon={getStatusIcon()}
          label={visit.status.toUpperCase()}
        />
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {/* Visitor Information */}
          <StyledCard>
            <CardContent>
              <SectionTitle variant="h5">Visitor Information</SectionTitle>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Person color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Full Name" 
                    secondary={`${visit.visitor_first_name} ${visit.visitor_last_name}`} 
                  />
                </ListItem>
                <Divider component="li" />

                <ListItem>
                  <ListItemIcon>
                    <Phone color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Phone Number" 
                    secondary={visit.visitor_phone} 
                  />
                </ListItem>
                <Divider component="li" />

                <ListItem>
                  <ListItemIcon>
                    <Email color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email Address" 
                    secondary={visit.visitor_email} 
                  />
                </ListItem>
                <Divider component="li" />

                <ListItem>
                  <ListItemIcon>
                    <CalendarToday color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Scheduled Date & Time" 
                    secondary={formattedDate} 
                  />
                </ListItem>
              </List>
            </CardContent>
          </StyledCard>

          {/* Visit Notes */}
          {visit.note && (
            <StyledCard sx={{ mt: 3 }}>
              <CardContent>
                <SectionTitle variant="h5">Visit Notes</SectionTitle>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography>{visit.note}</Typography>
                </Paper>
              </CardContent>
            </StyledCard>
          )}

          {/* Payment Attachment */}
          {visit.payment_attachment && (
           <StyledCard sx={{ mt: 3 }}>
           <CardContent>
             <SectionTitle variant="h5">Payment Receipt</SectionTitle>
             <Box 
               display="flex" 
               alignItems="center" 
               gap={3} 
               mt={2}
               sx={{
                 flexDirection: { xs: 'column', sm: 'row' },
                 alignItems: { xs: 'flex-start', sm: 'center' }
               }}
             >
               {/* Image Preview Thumbnail */}
               <Box
                 sx={{
                   width: 200,
                   height: 200,
                   borderRadius: 1,
                   overflow: 'hidden',
                   border: '1px solid',
                   borderColor: 'divider',
                   cursor: 'pointer',
                   '&:hover': {
                     boxShadow: 2,
                   }
                 }}
                 onClick={() => window.open(visit.payment_attachment, '_blank')}
               >
                 <img
                   src={visit.payment_attachment}
                   alt="Payment receipt thumbnail"
                   style={{
                     width: '100%',
                     height: '100%',
                     objectFit: 'cover',
                   }}
                 />
               </Box>
         
               {/* Action Buttons */}
               <Box display="flex" gap={2}>
                 <Button
                   variant="outlined"
                   startIcon={<AttachFile />}
                   href={visit.payment_attachment}
                   target="_blank"
                   rel="noopener noreferrer"
                   sx={{ minWidth: 180 }}
                 >
                   View Full Receipt
                 </Button>
            
               </Box>
             </Box>
           </CardContent>
         </StyledCard>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Project Information */}
          <StyledCard>
            <CardContent>
              <SectionTitle variant="h5">Project Details</SectionTitle>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Home color="primary" />
                <Typography variant="h6">{visit.project.title}</Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                {visit.project.short_description || visit.project.description.substring(0, 150) + '...'}
              </Typography>

              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => navigate(`/projects/${visit.project.id}`)}
              >
                View Project Details
              </Button>
            </CardContent>
          </StyledCard>

          {/* Project Amenities */}
          {visit.project.amenities && visit.project.amenities.length > 0 && (
            <StyledCard sx={{ mt: 3 }}>
              <CardContent>
                <SectionTitle variant="h5">Project Amenities</SectionTitle>
                <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
                  {visit.project.amenities.map((amenity: string, index: number) => (
                    <Chip 
                      key={index} 
                      label={amenity} 
                      variant="outlined" 
                      color="primary"
                      size="small"
                    />
                  ))}
                </Box>
              </CardContent>
            </StyledCard>
          )}


        </Grid>
      </Grid>

      {/* Action Buttons */}
      {/* <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
        <Button variant="outlined" color="error">
          Cancel Visit
        </Button>
        <Button variant="contained" color="primary">
          Confirm Visit
        </Button>
      </Box> */}
    </Box>
  );
};

export default ScheduledVisitDetail;